<template>
  <AppHeader />
  <main class="main-content">
    <router-view />
  </main>
  <AppFooter />
  <CookieBanner ref="cookieBanner" />
</template>

<script>
import AppHeader from './components/Header.vue';
import AppFooter from './components/Footer.vue';
import CookieBanner from './components/CookieBanner.vue';

export default {
  name: 'App',
  components: {
    AppHeader,
    AppFooter,
    CookieBanner
  },
  methods: {
    openCookieSettings() {
      this.$refs.cookieBanner.open(true);
    }
  },
  provide() {
    return {
      openCookieSettings: this.openCookieSettings
    };
  }
}
</script>

<style>
:root {
  --font-primary: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  --font-secondary: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;

  --color-primary: #8c52ff;
  --color-primary-dark: #7a3de0;
  --color-primary-light: #ede8ff;
  --color-primary-bg: #f8f5ff;

  --color-secondary: #1a1a2e;
  --color-secondary-light: #4a4a6a;

  --color-background: #ffffff;
  --color-background-alt: #f8f9fa;
  --color-background-accent: #f0ecff;

  --color-text: #1a1a2e;
  --color-text-muted: #6b7280;
  --color-border: #e5e7eb;
  --color-shadow: rgba(0, 0, 0, 0.06);

  --spacing-xs: 4px;
  --spacing-sm: 8px;
  --spacing-md: 16px;
  --spacing-lg: 32px;
  --spacing-xl: 48px;
  --spacing-xxl: 80px;

  --border-radius-sm: 6px;
  --border-radius-md: 10px;
  --border-radius-lg: 16px;
  --border-radius-xl: 24px;

  --shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.08);
  --shadow-md: 0 4px 12px rgba(0, 0, 0, 0.08);
  --shadow-lg: 0 8px 30px rgba(0, 0, 0, 0.1);
}

*,
*::before,
*::after {
  box-sizing: border-box;
}

body {
  margin: 0;
  font-family: var(--font-primary);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: var(--color-text);
  background-color: var(--color-background);
  line-height: 1.7;
  overflow-x: hidden;
  width: 100%;
}

#app {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  overflow-x: hidden;
}

.main-content {
  flex: 1;
  width: 100%;
  overflow-x: hidden;
}

a {
  color: var(--color-primary);
  text-decoration: none;
  transition: color 0.2s ease-in-out;
}

a:hover {
  color: var(--color-primary-dark);
}

section {
  padding-top: var(--spacing-xxl);
  padding-bottom: var(--spacing-xxl);
  position: relative;
}

section::before,
section::after {
  display: none;
}

.container {
  width: 100%;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  padding-left: 24px;
  padding-right: 24px;
  box-sizing: border-box;
}

@media (max-width: 768px) {
  .container {
    padding-left: 16px;
    padding-right: 16px;
  }
  section {
    padding-top: 48px;
    padding-bottom: 48px;
  }
}

h1, h2, h3, h4, h5, h6 {
  font-family: var(--font-secondary);
  color: var(--color-secondary);
  margin-top: 0;
  margin-bottom: var(--spacing-md);
  line-height: 1.2;
  font-weight: 700;
}

h1 { font-size: 3.2em; letter-spacing: -0.02em; }
.section-title, h2 { font-size: 2.4em; margin-bottom: var(--spacing-xl); text-align: center; letter-spacing: -0.02em; }
h3 { font-size: 1.4em; }
h4 { font-size: 1.15em; }

.section-subtitle {
  text-align: center;
  color: var(--color-text-muted);
  font-size: 1.15em;
  max-width: 640px;
  margin: -32px auto 48px;
  line-height: 1.7;
}

.text-center {
  text-align: center;
}

.cta-button {
  display: inline-block;
  background-color: var(--color-primary);
  color: white !important;
  padding: 14px 32px;
  text-decoration: none;
  font-size: 1em;
  font-weight: 600;
  border-radius: var(--border-radius-md);
  transition: all 0.25s ease;
  border: none;
  cursor: pointer;
  letter-spacing: 0.01em;
}

.cta-button:hover {
  background-color: var(--color-primary-dark);
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(140, 82, 255, 0.3);
  color: white !important;
}

.cta-button.secondary {
  background-color: transparent;
  color: var(--color-primary) !important;
  border: 2px solid var(--color-primary);
}

.cta-button.secondary:hover {
  background-color: var(--color-primary-light);
  color: var(--color-primary-dark) !important;
  border-color: var(--color-primary-dark);
  box-shadow: none;
}

.bg-alt {
  background-color: var(--color-background-alt);
}

.bg-accent {
  background-color: var(--color-background-accent);
}
</style>
